import {Chip} from '@dropbox/dig-components/chip';
import {Box, Cluster, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AiFill} from '@dropbox/dig-icons/assets';
import {logAiSearchInteraction} from 'analytics/aiSearchAnalytics';
import {AI_SEARCH_SUGGESTIONS} from 'components/ai/aiQueryConversation';
import {type AskSuggestionKind, useAskSuggestions} from 'components/ai/useAskSuggestions';
import {AvatarIcon} from 'components/DSYS/Avatar';
import {CompanyAskGlyph} from 'components/DSYS/CompanyOSLogo';
import {EmptyState} from 'components/DSYS/EmptyState';
import {t} from 'i18next';

import styles from './AiSearchDrawer.module.css';

type EmptyStateSuggestion = {question: string; kind?: AskSuggestionKind};

const LEGACY_SUGGESTIONS: EmptyStateSuggestion[] = AI_SEARCH_SUGGESTIONS.map((question) => ({
  question,
}));

const SuggestionChip = ({
  suggestion,
  index,
  onAsk,
}: {
  suggestion: EmptyStateSuggestion;
  index: number;
  onAsk: (suggestion: EmptyStateSuggestion) => void;
}) => (
  <Chip
    variant="standard"
    size="small"
    onClick={() => {
      logAiSearchInteraction({
        action: 'suggestion_click',
        source: 'drawer',
        suggestionIndex: index,
        suggestionKind: suggestion.kind,
      });
      onAsk(suggestion);
    }}
    {...withShade({})}
  >
    <Chip.IconAccessory>
      <UIIcon src={AiFill} />
    </Chip.IconAccessory>
    <Chip.Content>{suggestion.question}</Chip.Content>
  </Chip>
);

/**
 * The Ask entry redesign's four questions, led by a workstream that needs you
 * if one does, wrapping in a centered line or two under the composer. Its own
 * component so the workstream request only runs while it's on screen, not
 * whenever the drawer is mounted.
 */
export const AskSuggestionChips = ({
  onAsk,
}: {
  onAsk: (suggestion: EmptyStateSuggestion) => void;
}) => {
  const suggestions = useAskSuggestions();
  return (
    <Cluster gap="8" alignX="center" paddingTop="16">
      {suggestions.map((suggestion, index) => (
        <Cluster.Item key={suggestion.question}>
          <SuggestionChip suggestion={suggestion} index={index} onAsk={onAsk} />
        </Cluster.Item>
      ))}
    </Cluster>
  );
};

/**
 * The Ask drawer before the first question. With `redesign` (Ask entry
 * redesign flag) it says what Ask is for now, and the drawer puts
 * AskSuggestionChips under the composer; otherwise it's the old copy with
 * its chips stacked under it.
 */
export const AiSearchEmptyState = ({
  redesign,
  onAsk,
}: {
  redesign: boolean;
  onAsk: (suggestion: EmptyStateSuggestion) => void;
}) => (
  <Stack gap="16">
    <EmptyState
      hideBorder
      // The drawer places the redesign itself, so it drops the page padding.
      {...(redesign ? {paddingY: '0', marginBottom: '16'} : {})}
      image={
        <Box display="flex" justifyContent="center">
          <AvatarIcon src={CompanyAskGlyph} />
        </Box>
      }
      title={t('ai_search_empty_title')}
      body={
        redesign ? (
          <Box className={styles.balancedText}>{t('ai_search_empty_body_capabilities')}</Box>
        ) : (
          <Stack gap="8">
            <Box paddingBottom="16">{t('ai_search_empty_body')}</Box>
            {LEGACY_SUGGESTIONS.map((suggestion, index) => (
              <Box key={suggestion.question}>
                <SuggestionChip suggestion={suggestion} index={index} onAsk={onAsk} />
              </Box>
            ))}
          </Stack>
        )
      }
    />
  </Stack>
);
